import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Mail } from "lucide-react";
import { Button } from "../../../../ui/components/Button";
import { Card } from "../../../../ui/components/Card";
import { Callout } from "../../../../ui/components/Callout";
import { AuthBrand } from "../../../../ui/components/AuthBrand";
import { EmailInput } from "../../../../ui/components/Form";
import * as authText from "../../../../ui/components/Auth/authTypography.style.css";
import { GoogleIcon } from "../../../../ui/components/Auth/GoogleIcon";
import * as authApi from "../../../lib/api/auth";
import { getProAppOrigin } from "../../../lib/auth/app-origins";
import { useI18n } from "../../../lib/i18n";

const GOOGLE_DOMAINS = new Set(["gmail.com", "googlemail.com"]);

function isGoogleEmail(email: string): boolean {
  const domain = email.split("@")[1]?.toLowerCase();
  return GOOGLE_DOMAINS.has(domain ?? "");
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const { t, tx } = useI18n();

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSend = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authApi.requestPasswordReset({ email });
      setSent(true);
    } catch (err: any) {
      setError(err.message || t("account.forgotPassword.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await authApi.requestPasswordReset({ email });
    } catch {
      // silently ignore resend errors
    } finally {
      setResendLoading(false);
    }
  };

  const handleReset = () => {
    setSent(false);
    setEmail("");
    setError(null);
  };

  if (sent) {
    return (
      <Card className={authText.authCardScroll}>
        <AuthBrand />

        <div className={authText.setupStepPanel}>
          <div className={authText.stepIconCircle}>
            <Mail size={24} />
          </div>

          <h1 className={authText.titleLg}>{tx("account.forgotPassword.checkInboxTitle")}</h1>
          <p className={authText.subtitleCentered} style={{ marginBottom: 16 }}>
            {tx("account.forgotPassword.checkInboxBodyPrefix")}{" "}
            <strong className={authText.strong}>{email}</strong>
            {tx("account.forgotPassword.checkInboxBodySuffix")}
          </p>

          {isGoogleEmail(email) && (
            <div style={{ textAlign: "left", width: "100%" }}>
              <Callout variant="info" icon={<GoogleIcon size={16} />}>
                {tx("account.forgotPassword.googleCalloutText")}{" "}
                <a
                  href="https://myaccount.google.com/signinoptions/password"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={authText.link}
                >
                  {tx("account.forgotPassword.resetViaGoogle")}
                </a>
              </Callout>
            </div>
          )}

          <Button
            variant="outline"
            color="neutral"
            fullWidth
            size="lg"
            onClick={handleResend}
            loading={resendLoading}
          >
            {tx("account.forgotPassword.resendEmail")}
          </Button>

          {import.meta.env.DEV && (
            <a
              href={`${getProAppOrigin()}/captured-emails`}
              target="_blank"
              rel="noopener noreferrer"
              className={authText.link}
              style={{ fontSize: 12 }}
            >
              View local email captures
            </a>
          )}
        </div>

        <p className={authText.footer}>
          <button type="button" className={authText.textButton} onClick={handleReset}>
            {tx("account.forgotPassword.useDifferentEmail")}
          </button>
        </p>
      </Card>
    );
  }

  return (
    <Card className={authText.authCardScroll}>
      <AuthBrand />

      <div className={authText.setupStepPanel}>
        <div className={authText.stepIconCircle}>
          <Mail size={24} />
        </div>

        <h1 className={authText.titleLg}>{tx("account.forgotPassword.title")}</h1>
        <p className={authText.subtitleCentered} style={{ marginBottom: 0 }}>
          {tx("account.forgotPassword.subtitle")}
        </p>
      </div>

      <form onSubmit={handleSend} style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 24, width: "100%" }}>
        {error && <Callout variant="error">{error}</Callout>}

        <EmailInput
          label={t("account.forgotPassword.emailLabel")}
          placeholder={t("account.forgotPassword.emailPlaceholder")}
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <Button
          type="submit"
          color="primary"
          variant="solid"
          fullWidth
          size="lg"
          loading={loading}
          disabled={!isValidEmail}
        >
          {tx("account.forgotPassword.sendResetLink")}
        </Button>
      </form>

      <p className={authText.footer}>
        <Link to="/login" className={authText.link}>
          {tx("account.forgotPassword.backToSignIn")}
        </Link>
      </p>
    </Card>
  );
}
